import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import en from '../locales/en.json';
import tr from '../locales/tr.json';
import es from '../locales/es.json';
import de from '../locales/de.json';
import it from '../locales/it.json';
import fr from '../locales/fr.json';

type TranslationObject = typeof en;
type LanguageCode = 'en' | 'tr' | 'es' | 'de' | 'it' | 'fr';

interface LanguageContextType {
  language: string;
  changeLanguage: (lang: string) => void;
  translations: Record<LanguageCode, TranslationObject>;
  getTranslation: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<LanguageCode>('en');
  
  const translations: Record<LanguageCode, TranslationObject> = {
    en,
    tr,
    es,
    de,
    it,
    fr
  };

  // Load saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const validLanguages: LanguageCode[] = ['en', 'tr', 'es', 'de', 'it', 'fr'];
    if (savedLanguage && validLanguages.includes(savedLanguage as LanguageCode)) {
      setLanguage(savedLanguage as LanguageCode);
    } else {
      setLanguage('en');
      localStorage.setItem('language', 'en');
    }
  }, []);

  // Change language function
  const changeLanguage = (lang: string) => {
    const validLanguages: LanguageCode[] = ['en', 'tr', 'es', 'de', 'it', 'fr'];
    if (validLanguages.includes(lang as LanguageCode)) {
      setLanguage(lang as LanguageCode);
      localStorage.setItem('language', lang);
    }
  };

  // Helper function to get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): string | undefined => {
    const keys = path.split('.');
    let current: any = obj;
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }
    
    return typeof current === 'string' ? current : undefined;
  };

  // Get translation with parameter support and nested key support
  const getTranslation = (key: string, params: Record<string, string | number> = {}): string => {
    const currentTranslations = translations[language];
    if (!currentTranslations) {
      return key;
    }

    // Get translation value (supports nested keys like "auth.login")
    let translation = getNestedValue(currentTranslations, key);

    // If still not found, return the key
    if (!translation) {
      return key;
    }

    // Replace parameters in translation string
    Object.keys(params).forEach((paramKey) => {
      translation = translation!.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(params[paramKey])
      );
    });

    return translation;
  };

  return (
    <LanguageContext.Provider
      value={{ language, changeLanguage, translations, getTranslation }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

